import {
  Checkbox as MantineCheckbox,
  CheckboxGroup,
  Stack,
  type CheckboxProps,
} from "@mantine/core";
import { useState, type ReactNode } from "react";
import styles from "./Checkbox.module.scss";
type CheckboxSize = "large" | "small";
type CheckboxState = "deselected" | "intermediate" | "selected";
type CheckboxItem = {
  label: ReactNode;
  value: string;
  disabled?: boolean;
  description?: ReactNode;
  state?: CheckboxState;
};
type BaseProps = Omit<
  CheckboxProps,
  "size" | "checked" | "indeterminate" | "children" | "value" | "onChange"
> & {
  size?: CheckboxSize;
  required?: boolean;
};
type SingleProps = BaseProps & {
  state?: CheckboxState;
  onValueChange?: (next: boolean) => void;
  options?: never;
  selectedValues?: never;
  onValuesChange?: never;
};
type ListProps = BaseProps & {
  options: CheckboxItem[];
  selectedValues?: string[];
  onValuesChange?: (next: string[]) => void;
  groupLabel?: ReactNode;
  groupDescription?: ReactNode;
  state?: never;
};
export type BrandCheckboxProps = SingleProps | ListProps;
export function Checkbox(props: BrandCheckboxProps) {
  const { size = "small", disabled } = props;
  const mantineSize = size === "large" ? "md" : "sm";
  const bodyGapClass = size === "large" ? styles.bodyGapLarge : styles.bodyGapSmall;
  const [internalState, setInternalState] = useState<CheckboxState>("deselected");
  const [internalValues, setInternalValues] = useState<string[]>([]);

  const getInputClass = (isDisabled: boolean, isChecked: boolean, isIndeterminate: boolean) => {
    if (isDisabled && (isChecked || isIndeterminate))
      return `${styles.input} ${styles.inputDisabledChecked}`;
    if (isDisabled) return `${styles.input} ${styles.inputDisabled}`;
    if (isChecked || isIndeterminate) return `${styles.input} ${styles.inputChecked}`;
    return styles.input;
  };

  if (!("options" in props)) {
    const { state, onValueChange, disabled, label, required, ...singleRest } = props;
    const isControlled = state !== undefined;
    const effectiveState = isControlled ? state : internalState;
    const checked = effectiveState === "selected";
    const indeterminate = effectiveState === "intermediate";
    const handleChange = (nextChecked: boolean) => {
      if (!isControlled) {
        setInternalState(nextChecked ? "selected" : "deselected");
      }
      onValueChange?.(nextChecked);
    };
    return (
      <MantineCheckbox
        {...singleRest}
        label={
          required ? (
            <>
              {label}
              <span style={{ color: "var(--text-danger-default)" }}> *</span>
            </>
          ) : (
            label
          )
        }
        disabled={disabled}
        size={mantineSize}
        checked={checked}
        indeterminate={indeterminate}
        onChange={(e) => handleChange(e.currentTarget.checked)}
        classNames={{
          body: bodyGapClass,
          label: disabled ? styles.labelDisabled : styles.label,
          input: getInputClass(!!disabled, checked, indeterminate),
          icon: styles.icon,
          description: styles.description,
        }}
      />
    );
  }
  const { options, selectedValues, onValuesChange, groupLabel, groupDescription, required } =
    props as ListProps;
  const isGroupControlled = selectedValues !== undefined;
  const effectiveValues = isGroupControlled ? selectedValues : internalValues;
  const handleGroupChange = (next: string[]) => {
    if (!isGroupControlled) {
      setInternalValues(next);
    }
    onValuesChange?.(next);
  };
  return (
    <CheckboxGroup
      value={effectiveValues}
      onChange={handleGroupChange}
      label={
        required ? (
          <>
            {groupLabel}
            <span style={{ color: "var(--text-danger-default)" }}> *</span>
          </>
        ) : (
          groupLabel
        )
      }
      description={groupDescription}
      className={styles.groupContainer}
    >
      <Stack className={styles.stackContainer}>
        {options.map((item) => {
          const isDisabled = disabled || item.disabled;
          const isChecked =
            item.state === "selected"
              ? true
              : item.state === "deselected"
                ? false
                : effectiveValues.includes(item.value);
          const isIndeterminate = item.state === "intermediate";
          return (
            <MantineCheckbox
              key={item.value}
              value={item.value}
              label={item.label}
              description={item.description}
              size={mantineSize}
              disabled={isDisabled}
              checked={isChecked}
              indeterminate={isIndeterminate}
              classNames={{
                body: bodyGapClass,
                label: isDisabled ? styles.labelDisabled : styles.label,
                input: getInputClass(!!isDisabled, isChecked, isIndeterminate),
                icon: styles.icon,
              }}
            />
          );
        })}
      </Stack>
    </CheckboxGroup>
  );
}
